/**
 * Formatters shared by the resource and asset views.
 *
 * Each of these existed three or four times over - `ResourceTable`,
 * `ResourceDetail`, `AssetCard`, `AssetResourcesTable` - and had already
 * drifted: one of the copies wrote dates with English month names.
 */

/** Date only, as Italian reads it: 10/09/2026. */
export const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Date and time, for audit-style rows. */
export const formatDateTime = (date: string | null | undefined): string => {
  if (!date) return '-'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleString('it-IT')
}

/** `count` carries no unit; everything else prints one. */
export const formatValue = (value: number | null | undefined, valueType?: string | null): string => {
  if (value === null || value === undefined) return '-'
  if (!valueType || valueType === 'count') return String(value)
  return `${value} ${valueType}`
}

const TECHNICAL_OPTION_CODES = [
  'npiv_protocol', 'vscsi_target', 'mpio_dual_vios',
  'san_storage_matched', 'multi_array_mirror', 'vtl_tape_npiv'
]

/** Technical options describe how a resource is wired; commercial ones are sold. */
export const isTechnicalOption = (opt: { code?: string; category?: string }): boolean => {
  if (opt.category === 'technical') return true
  if (opt.category === 'commercial') return false
  return TECHNICAL_OPTION_CODES.includes(opt.code || '')
}

export interface ResourceOption {
  code?: string
  name?: string
  status?: string
  category?: string
}

/**
 * Options reach the lists either as an array of tags or as a JSON object of
 * specifications, which only the resource detail renders. Anything that is not
 * an array has no tags to show.
 */
export const filterOptionsByPrefs = (
  options: unknown,
  prefs: { showTechnical: boolean; showCommercial: boolean }
): ResourceOption[] => {
  if (!Array.isArray(options)) return []
  return options.filter(opt =>
    isTechnicalOption(opt) ? prefs.showTechnical : prefs.showCommercial
  )
}

/**
 * Importi in euro, come li legge chi firma una fattura: 13.792,50 €.
 *
 * Il backend manda stringhe e non numeri, perche' sui soldi un `float` perde
 * centesimi: qui si converte una volta sola, in fondo, per mostrarlo.
 */
export const formatEuro = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '-'
  return n.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

/** Quantita' senza code di zeri: `2.0000` diventa `2`, `0.5300` resta `0,53`. */
export const formatQuantita = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '-'
  return n.toLocaleString('it-IT', { maximumFractionDigits: 4 })
}

const MESI = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
]

/** `2026-09` si legge "settembre 2026", non "2026-09". */
/**
 * Un prezzo di listino, con i decimali che ha: da due a quattro.
 *
 * Non e' `formatEuro`, che arrotonda al centesimo: quello va bene per un
 * importo, che in fattura si arrotonda davvero, ma su un prezzo mostra un
 * numero diverso da quello con cui si fattura (1,365 € diventava 1,37 €).
 */
export const formatPrezzo = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '-'
  return n.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  })
}

/** `1 uscita`, `2 uscite`, `0 uscite`: il numero con la sua parola accordata. */
export const plurale = (n: number, uno: string, molti: string): string =>
  `${n} ${n === 1 ? uno : molti}`

/** «ad agosto», «a settembre»: la preposizione davanti al nome di un mese. */
export const conArticolo = (testo: string): string =>
  `${/^[aeiou]/i.test(testo) ? 'ad' : 'a'} ${testo}`

export const formatPeriodo = (period: string | null | undefined): string => {
  if (!period) return '-'
  const [anno, mese] = period.split('-')
  const nome = MESI[Number(mese) - 1]
  return nome ? `${nome} ${anno}` : period
}

/** `2026-Q3` si legge "3° trimestre 2026". */
export const formatTrimestre = (quarter: string | null | undefined): string => {
  if (!quarter) return '-'
  const [anno, q] = quarter.split('-Q')
  return q ? `${q}° trimestre ${anno}` : quarter
}

/** Il mese di oggi, `YYYY-MM`. */
export const periodoCorrente = (): string => ultimiMesi(1)[0]

/**
 * L'avviso per chi chiude mesi non ancora finiti, o '' se sono tutti finiti.
 * Il backend li rifiuta senza `anticipata`: questo e' il testo che la chiede.
 */
export const avvisoNonFinito = (mesi: string[], corrente: string = periodoCorrente()): string => {
  const inCorso = mesi.filter(m => m >= corrente)
  if (!inCorso.length) return ''
  const nomi = inCorso.map(formatPeriodo).join(', ')
  return inCorso.length === 1
    ? `${nomi} non è ancora finito: le quantità sono quelle di oggi, non di fine mese.`
    : `${nomi} non sono ancora finiti: le quantità sono quelle di oggi, non di fine mese.`
}

/** Gli ultimi `n` mesi, dal piu' recente: i selettori di periodo. */
export const ultimiMesi = (n: number): string[] => {
  const oggi = new Date()
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(oggi.getFullYear(), oggi.getMonth() - i, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })
}

/** Le iniziali di un nome per un avatar senza foto: "Anna Bianchi" -> "AB", "admin" -> "AD". */
export const iniziali = (nome: string | null | undefined): string => {
  const parole = String(nome ?? '').trim().split(/\s+/).filter(Boolean)
  if (parole.length >= 2) return `${parole[0][0]}${parole[1][0]}`.toUpperCase()
  return (parole[0] ?? '').substring(0, 2).toUpperCase()
}
